import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import {
  Alert,
  DataTable,
  Empty,
  Kpi,
  KpiGrid,
  Muted,
  PageHeader,
  Section,
  StatusBadge,
} from '@/components/ui'
import { assinarDocumento, fetchGedDocumentos, type DocumentoItem } from '@/features/ops/api'
import { formatDate } from './utils'

function tone(status?: string) {
  const s = (status || '').toLowerCase()
  if (/(pend|aguard)/.test(s)) return 'pendente' as const
  if (/(assin|aprov|ok|dispon)/.test(s)) return 'ativo' as const
  if (/(recus|rejeit|expir)/.test(s)) return 'inativo' as const
  return 'pendente' as const
}

function tituloDoc(d: DocumentoItem) {
  const tipo = typeof d.tipo === 'object' ? d.tipo?.nome : d.tipo
  return String(d.titulo || d.nome || tipo || 'Documento')
}

export function AssinaturaPage() {
  const { canEdit, isRH } = useAuth()
  const editable = canEdit('assinatura-digital') || isRH
  const qc = useQueryClient()
  const [filtro, setFiltro] = useState('')
  const [busca, setBusca] = useState('')
  const [msg, setMsg] = useState('')
  const [msgTone, setMsgTone] = useState<'success' | 'danger' | 'info'>('info')

  const docs = useQuery({
    queryKey: ['dp', 'assinatura', 'ged', filtro],
    queryFn: () =>
      fetchGedDocumentos({
        status: filtro || undefined,
        limit: 120,
      }),
  })

  const lista = useMemo(() => {
    const q = busca.trim().toLowerCase()
    let rows = docs.data?.dados || []
    if (!filtro) {
      rows = rows.filter((d) =>
        /pendente_assinatura|assinado|recusado|rejeitado|disponivel/i.test(String(d.status || '')),
      )
    }
    if (q) {
      rows = rows.filter((d) => {
        const t = tituloDoc(d).toLowerCase()
        const n = String(d.colaborador_nome || '').toLowerCase()
        return t.includes(q) || n.includes(q)
      })
    }
    return rows
  }, [docs.data, filtro, busca])

  const pendentes = lista.filter((d) => /pendente_assinatura/i.test(String(d.status || ''))).length
  const assinados = lista.filter((d) => /assinado/i.test(String(d.status || ''))).length
  const taxa = lista.length ? Math.round((assinados / lista.length) * 100) : 0

  const assinarMut = useMutation({
    mutationFn: (id: string) => assinarDocumento(id),
    onSuccess: async () => {
      setMsgTone('success')
      setMsg('Documento assinado com trilha de auditoria.')
      await qc.invalidateQueries({ queryKey: ['dp', 'assinatura'] })
    },
    onError: (err) => {
      setMsgTone('danger')
      setMsg(err instanceof ApiError ? err.message : 'Falha ao assinar.')
    },
  })

  return (
    <section className="hi-page">
      <PageHeader
        title="Assinatura digital"
        subtitle="Fila de holerites, férias, admissão e rescisão — aceite eletrônico via GED."
        actions={
          <>
            <Link className="btn btn-secondary" to="/app/documentos">
              Abrir GED
            </Link>
            <a className="btn btn-secondary" href={legacyAppUrl('#/assinatura-digital')}>
              Legado
            </a>
          </>
        }
      />

      {msg ? <Alert tone={msgTone}>{msg}</Alert> : null}

      <KpiGrid>
        <Kpi label="Na fila" value={String(lista.length)} />
        <Kpi label="Pendentes" value={String(pendentes)} />
        <Kpi label="Assinados" value={String(assinados)} />
        <Kpi label="Taxa" value={`${taxa}%`} />
      </KpiGrid>

      <form
        className="hi-filters"
        onSubmit={(e) => {
          e.preventDefault()
        }}
      >
        <input
          type="search"
          placeholder="Buscar colaborador ou documento…"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <select value={filtro} onChange={(e) => setFiltro(e.target.value)} aria-label="Status">
          <option value="">Assinatura (todos relevantes)</option>
          <option value="pendente_assinatura">Pendentes</option>
          <option value="assinado">Assinados</option>
          <option value="rejeitado">Recusados/rejeitados</option>
          <option value="disponivel">Disponíveis</option>
        </select>
      </form>

      <Section title="Documentos para assinatura" flush>
        {docs.isLoading ? <Muted>Carregando…</Muted> : null}
        {docs.isError ? (
          <Alert tone="danger">
            {docs.error instanceof ApiError ? docs.error.message : 'Falha ao listar documentos.'}
          </Alert>
        ) : null}
        {!docs.isLoading && lista.length === 0 ? (
          <Empty>
            Nenhum documento na fila. Envie pelo GED com status “pendente de assinatura”.
          </Empty>
        ) : null}
        {lista.length > 0 ? (
          <DataTable>
            <thead>
              <tr>
                <th>Documento</th>
                <th>Colaborador</th>
                <th>Enviado</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lista.map((d) => (
                <tr key={d.id}>
                  <td>
                    <strong>{tituloDoc(d)}</strong>
                  </td>
                  <td>{d.colaborador_nome || '—'}</td>
                  <td>{formatDate(d.created_at)}</td>
                  <td>
                    <StatusBadge tone={tone(String(d.status))}>
                      {String(d.status_display || d.status || '—')}
                    </StatusBadge>
                  </td>
                  <td>
                    {editable && /pendente_assinatura|disponivel/i.test(String(d.status || '')) ? (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={assinarMut.isPending}
                        onClick={() => assinarMut.mutate(d.id)}
                      >
                        Assinar
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        ) : null}
      </Section>
    </section>
  )
}

export default AssinaturaPage
